import { IcebergIcon } from '@tradesolution/iceberg-ui-react';
import { CompareStatus, qaCompareSimpleValue } from 'pages/QA/components/QACardDetails/utils';
import { useState, useEffect } from 'react';
import { Button, Collapse, Row, Col, Form, InputGroup } from 'react-bootstrap';
import CompareIcon from '../../../CompareIcon';
import Style from '../../index.module.css';
import CopyToClipboardIcon from 'components/CopyToClipboardIcon';
import { addMinutes, format } from 'date-fns';
import { ChangeRequestValidator } from 'pages/QA/components/QACardDetails/ChangeRequestValidator';
import { NorwegianDatepicker } from 'components/NorwegianDatepicker';

interface Props {
  value: Date | null;
  onChange: (value: Date | null | undefined) => void;
  changeRequest: {
    oppstartsdato: Date | null;
    oppstartsdatoFromCr: Date | null;
  };
  error?: string;
}

const OppstartdatoRow = (props: Props) => {
  const [isExpandRow, setIsExpandRow] = useState<boolean>(false);
  const [compareStatus, setCompareStatus] = useState<CompareStatus>(
    qaCompareSimpleValue(
      props.changeRequest.oppstartsdato
        ? format(props.changeRequest.oppstartsdato, 'yyyy-MM-dd')
        : '',
      props.value ? format(props.value, 'yyyy-MM-dd') : '',
    ),
  );
  const [crValueError, setCrValueError] = useState<string | null>(null);

  const handleExpandRowClick = () => {
    setIsExpandRow(!isExpandRow);
  };

  const handleDateChanged = (date: Date | null | undefined) => {
    if (!date) {
      props.onChange(undefined);
      return;
    }
    const adjustedDate = addMinutes(date, -date.getTimezoneOffset());
    props.onChange(adjustedDate);
  };

  useEffect(() => {
    setCompareStatus(
      qaCompareSimpleValue(
        props.changeRequest.oppstartsdato
          ? format(props.changeRequest.oppstartsdato, 'yyyy-MM-dd')
          : '',
        props.value ? format(props.value, 'yyyy-MM-dd') : '',
      ),
    );
  }, [props.value, props.changeRequest.oppstartsdato]);

  useEffect(() => {
    if (compareStatus === 'ulik' || compareStatus === 'ny') {
      setIsExpandRow(true);
    }
  }, [compareStatus]);

  // Check if the value from the change request has any errors
  useEffect(() => {
    if (props.changeRequest.oppstartsdatoFromCr) {
      const error = ChangeRequestValidator.hasOppstartsDatoErrors(
        props.changeRequest.oppstartsdatoFromCr,
      );
      setCrValueError(error);
    } else {
      setCrValueError(null);
    }
  }, [props.changeRequest.oppstartsdatoFromCr]);

  return (
    <>
      <div className={Style.changeRequestRowHeaderContainer} onClick={handleExpandRowClick}>
        <h6 className={Style.changeRequestRowHeader}>
          {!isExpandRow && <CompareIcon compareStatus={compareStatus} size="sm" />}
          <span className={Style.changeRequestRowHeaderText}>Oppstartdato</span>
        </h6>
        <Button variant="link" size="sm">
          <IcebergIcon icon={isExpandRow ? 'chevron-up' : 'chevron-down'} bold />
        </Button>
      </div>
      <Collapse in={isExpandRow}>
        <div>
          <Row className={Style.formGroupRow}>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <InputGroup size="sm">
                  <Form.Control
                    placeholder="dd.mm.yyyy"
                    className={Style.formControl}
                    readOnly
                    value={
                      props.changeRequest.oppstartsdato
                        ? format(props.changeRequest.oppstartsdato, 'dd.MM.yyyy')
                        : ''
                    }
                    isValid={!crValueError && !!props.changeRequest.oppstartsdatoFromCr}
                    isInvalid={!!crValueError}
                  />
                  {props.changeRequest.oppstartsdato && (
                    <InputGroup.Text>
                      <CopyToClipboardIcon
                        val={format(props.changeRequest.oppstartsdato, 'dd.MM.yyyy')}
                      />
                    </InputGroup.Text>
                  )}
                  <Form.Control.Feedback type="invalid">
                    {crValueError ?? 'Oppstartsdato er ugyldig.'}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col xs="auto" md={2} className={Style.compareIconContainer}>
              {!!crValueError ? (
                <CompareIcon compareStatus="ugyldig" tooltipText={crValueError} />
              ) : (
                <CompareIcon
                  compareStatus={compareStatus}
                  onClick={() => handleDateChanged(props.changeRequest.oppstartsdato)}
                />
              )}
            </Col>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <NorwegianDatepicker
                  placeholder="dd.mm.yyyy"
                  value={props.value}
                  onChange={(date) => handleDateChanged(date)}
                  showFirstDaySuggestions
                />
              </Form.Group>
              {props.error && <Form.Text className="text-danger">{props.error}</Form.Text>}
            </Col>
          </Row>
        </div>
      </Collapse>
    </>
  );
};

export default OppstartdatoRow;
